import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { T, MONO, SERIF, alpha } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Uyarılar': 'Alerts',
  'Kampanyaların için özel uyarı kuralları tanımla ve üretilen uyarıları takip et.': 'Define custom alert rules for your campaigns and track the alerts generated.',
  'Görüldü olarak işaretle': 'Mark as seen',
  'Düzenle': 'Edit',
  'Uyarıyı Düzenle': 'Edit Alert',
  'Kaydet': 'Save',
  'Kaydediliyor…': 'Saving…',
  'Saatlik Kontrol': 'Hourly Check', 'Günlük Kontrol': 'Daily Check', 'Haftalık Kontrol': 'Weekly Check',
  'Uyarı güncellenemedi. Lütfen tekrar dene.': 'Could not update the alert. Please try again.',
  'Uyarı olarak kaydet': 'Save as alert',
  'Önerilen Kural': 'Suggested Rule',
  'Uyarıyı sil': 'Delete alert',
  'Uyarı kaydedildi.': 'Alert saved.',
  'Uyarı oluşturulamadı. Lütfen tekrar dene.': 'Could not create the alert. Please try again.',
  'Karşılaştırılan değer son 7 günün toplamı (oran metriklerinde ortalaması) olur.': 'The compared value is the last 7 days total (average for rate metrics).',
  'Kontrol Et': 'Check',
  'Kontrol': 'Check',
  'Hesap bağlı değil': 'Account not connected',
  'Uyarı Oluştur': 'Create Alert',
  'Toplam Uyarı': 'Total Alerts',
  'Aktif': 'Active',
  'Duraklatılmış': 'Paused',
  'Bugün Tetiklenen': 'Triggered Today',
  'Kritik': 'Critical',
  'Kayıtlı Uyarılar': 'Saved Alerts',
  'Uyarı Geçmişi': 'Alert History',
  'Yükleniyor…': 'Loading…',
  'Henüz uyarı kuralı yok.': 'No alert rules yet.',
  'Henüz tetiklenen uyarı yok.': 'No triggered alerts yet.',
  'Seçenekler': 'Options',
  'Etkinleştir': 'Activate',
  'Duraklat': 'Pause',
  'Sil': 'Delete',
  'E-posta gönderildi': 'Email sent',
  'Bugün tetiklendi': 'Triggered today',
  'Duraklatıldı': 'Paused',
  'Reklam kampanyalarını izlemek için özel bir uyarı kur.': 'Set up a custom alert to monitor your ad campaigns.',
  'UYARI ADI': 'ALERT NAME',
  'örn. Yüksek Harcama Uyarısı': 'e.g. High Spend Alert',
  'PLATFORM': 'PLATFORM',
  'KAPSAM': 'SCOPE',
  'Tüm kampanyalar': 'All campaigns',
  'METRİK': 'METRIC',
  'KOŞUL': 'CONDITION',
  'EŞİK': 'THRESHOLD',
  'örn. 5000': 'e.g. 5000',
  'KONTROL ARALIĞI': 'CHECK INTERVAL',
  'UYARI TÜRÜ': 'ALERT TYPE',
  'Vazgeç': 'Cancel',
  'Oluşturuluyor…': 'Creating…',
  'Şu An': 'Currently',
  // metrikler
  'Harcama': 'Spend', 'Frekans': 'Frequency', 'CTR': 'CTR', 'CPC': 'CPC', 'Dönüşüm': 'Conversions', 'Gösterim': 'Impressions',
  'Tıklama': 'Clicks', 'Satın Alma': 'Purchases', 'Erişim': 'Reach', 'Oturum': 'Sessions',
  'Dönüşüm Oranı': 'Conversion Rate', 'Hemen Çıkma Oranı': 'Bounce Rate',
  // koşullar
  '> Büyüktür': '> Greater than', '< Küçüktür': '< Less than', '≥ Büyük eşit': '≥ Greater or equal',
  '≤ Küçük eşit': '≤ Less or equal', '= Eşittir': '= Equal to',
  // aralıklar
  'Saatlik': 'Hourly', 'Günlük': 'Daily', 'Haftalık': 'Weekly',
  // uyarı türleri
  'Bilgi': 'Info', 'Uyarı': 'Warning',
  'Düşük öncelikli uyarı': 'Low priority alert', 'Orta öncelikli uyarı': 'Medium priority alert', 'Yüksek öncelikli uyarı': 'High priority alert',
});

const API = '/api/v1';

type Rule = {
  id: number; title: string; meta: string; status: string;
  platform: string; scope?: string; scope_label?: string; metric: string;
  condition_op: string; threshold: number; check_interval: string; alert_type: string;
  icon: string; icon_bg: string; icon_color: string;
  last_value?: number; last_checked_at?: string;
};

type HistoryEvent = {
  suggested_rule?: { title?: string; platform: string; metric: string; condition_op: string; threshold: number } | null;
  source?: 'rule' | 'ai'; description?: string; what_to_check?: string; evidence?: string;
  id: number; rule_id: number; title: string; platform: string; alert_type: string;
  metric: string; metric_label: string; condition_op: string; condition_symbol: string;
  threshold: number; threshold_fmt: string; actual_value: number; actual_fmt: string; triggered_at: string;
  email_sent: number | boolean;
};

const PLATFORMS = [
  { value: 'gads', label: 'Google Ads' },
  { value: 'meta', label: 'Meta Ads' },
  { value: 'ga4', label: 'Google Analytics 4' },
];

const METRICS: Record<string, { value: string; label: string }[]> = {
  gads: [
    { value: 'spend', label: 'Harcama' }, { value: 'roas', label: 'ROAS' },
    { value: 'ctr', label: 'CTR' }, { value: 'cpc', label: 'CPC' },
    { value: 'conversions', label: 'Dönüşüm' }, { value: 'impressions', label: 'Gösterim' },
    { value: 'clicks', label: 'Tıklama' },
  ],
  meta: [
    { value: 'spend', label: 'Harcama' }, { value: 'roas', label: 'ROAS' },
    { value: 'ctr', label: 'CTR' }, { value: 'cpc', label: 'CPC' },
    { value: 'purchases', label: 'Satın Alma' }, { value: 'reach', label: 'Erişim' }, { value: 'frequency', label: 'Frekans' },
  ],
  ga4: [
    { value: 'sessions', label: 'Oturum' }, { value: 'conv_rate', label: 'Dönüşüm Oranı' },
    { value: 'bounce_rate', label: 'Hemen Çıkma Oranı' },
  ],
};

const CONDITIONS = [
  { value: 'gt', label: '> Büyüktür' }, { value: 'lt', label: '< Küçüktür' },
  { value: 'gte', label: '≥ Büyük eşit' }, { value: 'lte', label: '≤ Küçük eşit' },
  { value: 'eq', label: '= Eşittir' },
];
const CONDITION_SYMBOLS: Record<string, string> = { gt: '>', lt: '<', gte: '≥', lte: '≤', eq: '=' };

const INTERVALS = [
  { value: 'hourly', label: 'Saatlik' }, { value: 'daily', label: 'Günlük' }, { value: 'weekly', label: 'Haftalık' },
];

const ALERT_TYPES = [
  { value: 'info', label: 'Bilgi', desc: 'Düşük öncelikli uyarı', color: T.blue },
  { value: 'warning', label: 'Uyarı', desc: 'Orta öncelikli uyarı', color: T.amberText },
  { value: 'critical', label: 'Kritik', desc: 'Yüksek öncelikli uyarı', color: T.redText },
];
const ALERT_TYPE_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  critical: { bg: 'var(--py-red-tint)', color: T.redText, label: 'Kritik' },
  warning: { bg: 'var(--py-amber-tint-alt)', color: T.amberText, label: 'Uyarı' },
  info: { bg: 'var(--py-blue-tint)', color: T.blue, label: 'Bilgi' },
};

const STATUS_PILL: Record<string, { bg: string; color: string; opacity?: number }> = {
  'Aktif': { bg: 'var(--py-green-tint)', color: T.greenText },
  'Bugün tetiklendi': { bg: 'var(--py-amber-tint-alt)', color: T.amberText },
  'Duraklatıldı': { bg: 'var(--py-track-bg)', color: 'var(--py-text-mid-alt)', opacity: 0.65 },
};

const PLATFORM_ICON_SRC: Record<string, string> = {
  gads: '/google-ads-icon.png', meta: '/meta-icon.png', ga4: '/google-analytics-icon.png',
};

function timeAgo(iso: string, lang: 'tr'|'en' = 'tr'): string {
  const then = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z').getTime();
  const diffMin = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (lang === 'en') {
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin} minutes ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH} hours ago`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD} days ago`;
  }
  if (diffMin < 1) return 'az önce';
  if (diffMin < 60) return `${diffMin} dakika önce`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} saat önce`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD} gün önce`;
}

// Kanıt satırındaki metrik adlarının ilk harfleri büyük ("Dönüşüm oranı: 0.4" → "Dönüşüm Oranı: 0.4")
function trUpperFirst(w: string): string {
  const f = w.charAt(0);
  return (f === 'i' ? 'İ' : f === 'ı' ? 'I' : f.toUpperCase()) + w.slice(1);
}
function titleMetrics(ev: string): string {
  return ev.split(' · ').map(part => {
    const i = part.indexOf(': ');
    return i < 0 ? part : part.slice(0, i).split(' ').map(trUpperFirst).join(' ') + part.slice(i);
  }).join(' · ');
}

function todayStr(): string { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function daysAgoStr(n: number): string { const d = new Date(); d.setDate(d.getDate() - n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

function evalCondition(value: number | null, op: string, threshold: number): boolean {
  if (value == null) return false;
  if (op === 'gt') return value > threshold;
  if (op === 'lt') return value < threshold;
  if (op === 'gte') return value >= threshold;
  if (op === 'lte') return value <= threshold;
  if (op === 'eq') return value === threshold;
  return false;
}

function SeverityIcon({ type, size = 14 }: { type: string; size?: number }) {
  const style = ALERT_TYPE_STYLE[type] || ALERT_TYPE_STYLE.warning;
  if (type === 'info') return null;
  if (type === 'critical') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={style.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7.5v6" />
        <path d="M12 16.8v.1" />
      </svg>
    );
  }
  return <Icon.warningTriangle size={size} sw={2} color={style.color} />;
}

export default function Uyarilar({ clientId = 1, config }: { clientId?: number; config?: any }) {
  const { t, lang } = useLang();
  const [rules, setRules] = useState<Rule[]>([]);
  const [history, setHistory] = useState<HistoryEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'kayitli' | 'gecmis'>('gecmis');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const [form, setForm] = useState({
    title: '', platform: 'gads', scope: '', scopeLabel: '', metric: 'spend',
    condition_op: 'gt', threshold: '', check_interval: 'daily', alert_type: 'warning',
  });
  const [campaignOptions, setCampaignOptions] = useState<{ id: string; name: string }[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  const platforms = config?.platforms || {};
  const gadsCfg = platforms.gads || {};
  const metaCfg = platforms.meta || {};

  const load = () => {
    axios.get(`${API}/uyarilar/?client_id=${clientId}&lang=${lang}`).then(r => setRules(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  const loadHistory = () => {
    setHistoryLoading(true);
    axios.get(`${API}/uyarilar/history?client_id=${clientId}&lang=${lang}`).then(r => setHistory(r.data)).catch(() => {}).finally(() => setHistoryLoading(false));
  };
  useEffect(() => { load(); loadHistory(); }, [clientId, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gerçek platform verisine karşı aktif kuralları değerlendir (sayfa her açıldığında)
  useEffect(() => {
    if (rules.length === 0) return;
    const active = rules.filter(r => r.status !== 'Duraklatıldı');
    if (active.length === 0) return;
    const gadsCid = gadsCfg.customerId, metaAid = metaCfg.accountId;
    Promise.all([
      gadsCid ? axios.get(`${API}/gads/${gadsCid}/overview?start_date=${daysAgoStr(6)}&end_date=${todayStr()}`).catch(() => null) : Promise.resolve(null),
      metaAid ? axios.get(`${API}/meta/${metaAid}/overview?start_date=${daysAgoStr(6)}&end_date=${todayStr()}`).catch(() => null) : Promise.resolve(null),
      gadsCid && active.some(r => r.platform === 'gads' && r.scope) ? axios.get(`${API}/gads/${gadsCid}/campaigns?start_date=${daysAgoStr(6)}&end_date=${todayStr()}`).catch(() => null) : Promise.resolve(null),
      metaAid && active.some(r => r.platform === 'meta' && r.scope) ? axios.get(`${API}/meta/${metaAid}/campaigns?start_date=${daysAgoStr(6)}&end_date=${todayStr()}`).catch(() => null) : Promise.resolve(null),
    ]).then(([gOv, mOv, gCamp, mCamp]) => {
      const gadsOverview = gOv?.data || null;
      const metaRaw = mOv?.data; const metaOverview = metaRaw?.overview || metaRaw || null;
      const gList: any[] = Array.isArray(gCamp?.data) ? gCamp?.data : (gCamp?.data?.campaigns || []);
      const mList: any[] = Array.isArray(mCamp?.data) ? mCamp?.data : (mCamp?.data?.campaigns || []);
      active.forEach(rule => {
        let src: any = null;
        if (rule.platform === 'gads') src = rule.scope ? gList.find(c => String(c.id ?? c.campaign_id) === rule.scope) : gadsOverview;
        else if (rule.platform === 'meta') src = rule.scope ? mList.find(c => String(c.id ?? c.campaign_id) === rule.scope) : metaOverview;
        if (!src) return;
        const value = src[rule.metric] ?? (rule.metric === 'purchases' ? src.conversions : rule.metric === 'conversions' ? src.purchases : null);
        if (value == null) return;
        const triggered = evalCondition(value, rule.condition_op, rule.threshold);
        const wasTriggered = rule.status === 'Bugün tetiklendi';
        if (triggered !== wasTriggered || rule.last_value !== value) {
          axios.patch(`${API}/uyarilar/${rule.id}?client_id=${clientId}`, { triggered, last_value: value }).then(r => {
            setRules(rs => rs.map(x => x.id === rule.id ? r.data : x));
            if (triggered && !wasTriggered) loadHistory();
          }).catch(() => {});
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules.length, gadsCfg.customerId, metaCfg.accountId]);

  const loadCampaigns = (platform: string) => {
    setCampaignOptions([]);
    if (platform === 'gads' && gadsCfg.customerId) {
      setCampaignsLoading(true);
      axios.get(`${API}/gads/${gadsCfg.customerId}/campaigns?start_date=${daysAgoStr(30)}&end_date=${todayStr()}`)
        .then(r => { const list = Array.isArray(r.data) ? r.data : (r.data?.campaigns || []); setCampaignOptions(list.map((c: any) => ({ id: String(c.id ?? c.campaign_id), name: c.name || c.campaign_name || '—' }))); })
        .catch(() => {}).finally(() => setCampaignsLoading(false));
    } else if (platform === 'meta' && metaCfg.accountId) {
      setCampaignsLoading(true);
      axios.get(`${API}/meta/${metaCfg.accountId}/campaigns?start_date=${daysAgoStr(30)}&end_date=${todayStr()}`)
        .then(r => { const list = Array.isArray(r.data) ? r.data : (r.data?.campaigns || []); setCampaignOptions(list.map((c: any) => ({ id: String(c.id ?? c.campaign_id), name: c.name || c.campaign_name || '—' }))); })
        .catch(() => {}).finally(() => setCampaignsLoading(false));
    }
  };

  const openModal = () => {
    const first = (['gads', 'meta', 'ga4'] as const).find(v => (v === 'gads' ? gadsCfg.customerId : v === 'meta' ? metaCfg.accountId : (platforms.ga4 || {}).propertyId)) || 'gads';
    setForm({ title: '', platform: first, scope: '', scopeLabel: '', metric: (METRICS[first] || METRICS.gads)[0].value, condition_op: 'gt', threshold: '', check_interval: 'daily', alert_type: 'warning' });
    setCreateErr('');
    setEditingId(null);
    loadCampaigns(first);
    setShowModal(true);
  };
  const openEdit = (r: Rule) => {
    setForm({ title: r.title, platform: r.platform, scope: r.scope || '', scopeLabel: r.scope_label || '', metric: r.metric, condition_op: r.condition_op,
      threshold: String(r.threshold), check_interval: r.check_interval, alert_type: r.alert_type });
    setCreateErr('');
    setEditingId(r.id);
    loadCampaigns(r.platform);
    setShowModal(true);
  };

  const createRule = async () => {
    if (!form.threshold || isNaN(parseFloat(form.threshold))) return;
    setCreateErr('');
    setSaving(true);
    try {
      if (editingId) {
        const r = await axios.patch(`${API}/uyarilar/${editingId}?client_id=${clientId}`, {
          title: form.title || undefined, platform: form.platform, scope: form.scope || undefined, scope_label: form.scope ? form.scopeLabel : undefined,
          clear_scope: !form.scope, metric: form.metric, condition_op: form.condition_op, threshold: parseFloat(form.threshold),
          check_interval: form.check_interval, alert_type: form.alert_type,
        });
        setRules(rs => rs.map(x => x.id === editingId ? r.data : x));
        setShowModal(false);
        return;
      }
      const r = await axios.post(`${API}/uyarilar/?client_id=${clientId}`, {
        title: form.title || undefined, platform: form.platform, scope: form.scope || undefined,
        scope_label: form.scope ? form.scopeLabel : undefined, metric: form.metric, condition_op: form.condition_op,
        threshold: parseFloat(form.threshold), check_interval: form.check_interval, alert_type: form.alert_type,
      });
      setRules(rs => [r.data, ...rs]);
      setShowModal(false);
    } catch (e: any) { setCreateErr(e?.response?.status === 403 && e?.response?.data?.detail ? e.response.data.detail : (editingId ? t('Uyarı güncellenemedi. Lütfen tekrar dene.') : t('Uyarı oluşturulamadı. Lütfen tekrar dene.'))); } finally { setSaving(false); }
  };

  const togglePause = (r: Rule) => {
    const nextActive = r.status === 'Duraklatıldı';
    axios.patch(`${API}/uyarilar/${r.id}?client_id=${clientId}`, { is_active: nextActive })
      .then(res => setRules(rs => rs.map(x => x.id === r.id ? res.data : x))).catch(() => {});
  };

  const removeRule = (id: number) => {
    setRules(rs => rs.filter(r => r.id !== id));
    axios.delete(`${API}/uyarilar/${id}?client_id=${clientId}`).catch(() => load());
  };

  const total = rules.length;
  const activeCount = rules.filter(r => r.status !== 'Duraklatıldı').length;   // tetiklenmiş olanlar da aktiftir
  const pausedCount = rules.filter(r => r.status === 'Duraklatıldı').length;
  const triggeredToday = rules.filter(r => r.status === 'Bugün tetiklendi').length;
  const criticalCount = rules.filter(r => r.alert_type === 'critical').length;

  const metricOptions = METRICS[form.platform] || METRICS.gads;
  const platformConnected = (v: string) => v === 'gads' ? !!gadsCfg.customerId : v === 'meta' ? !!metaCfg.accountId : !!(platforms.ga4 || {}).propertyId;
  const markSeen = (h: HistoryEvent) => {
    setHistory(hs => hs.filter(x => !(x.id === h.id && (x.source || 'rule') === (h.source || 'rule'))));
    axios.post(`${API}/uyarilar/history/seen`, { client_id: clientId, source: h.source || 'rule', id: h.id }).catch(() => loadHistory());
  };
  const [createErr, setCreateErr] = useState('');
  const dropHistory = (h: HistoryEvent) => setHistory(hs => hs.filter(x => !(x.id === h.id && (x.source || 'rule') === (h.source || 'rule'))));
  // AI uyarısı: ✓ önerilen kuralı Kayıtlı Uyarılar'a kurar (kural yoksa yalnızca görüldü sayar), ✕ uyarıyı siler
  const acceptAi = (h: HistoryEvent) => {
    if (!h.suggested_rule) { markSeen(h); return; }
    dropHistory(h);
    axios.post(`${API}/uyarilar/history/ai/${h.id}/accept?client_id=${clientId}`)
      .then(r => { setRules(rs => [r.data, ...rs]); setToast(t('Uyarı kaydedildi.')); setTimeout(() => setToast(''), 2600); })
      .catch(() => loadHistory());
  };
  const [toast, setToast] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);

  function metricLabel(platform: string, metric: string) {
    return t((METRICS[platform] || METRICS.gads).find(m => m.value === metric)?.label || metric);
  }
  function fmtThreshold(metric: string, v: number) {
    if (metric === 'roas') return `${v}x`;
    if (metric === 'spend' || metric === 'cpc') return `₺${v}`;
    if (metric === 'ctr' || metric === 'conv_rate' || metric === 'bounce_rate') return `%${v}`;
    return String(v);
  }
  function conditionPillText(r: Rule) {
    return `${metricLabel(r.platform, r.metric)} ${CONDITION_SYMBOLS[r.condition_op] || r.condition_op} ${fmtThreshold(r.metric, r.threshold)}`;
  }
  // "Meta Ads · Günlük Kontrol" — kapsam yalnızca belirli bir kampanya seçiliyse yazılır
  function ruleMeta(r: Rule) {
    const plat = PLATFORMS.find(p => p.value === r.platform)?.label || r.platform;
    return [r.scope_label, plat, `${intervalLabel(r.check_interval)} ${t('Kontrol')}`].filter(Boolean).join(' · ');
  }
  function intervalLabel(v: string) {
    return t(INTERVALS.find(i => i.value === v)?.label || v);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' as const }}>
        <div>
          <h1 style={{ margin: 0, fontFamily: SERIF, fontSize: 'clamp(30px,3.4vw,38px)', fontWeight: 400, letterSpacing: '-.015em', lineHeight: 1.1 }}>{t('Uyarılar')}</h1>
          <p style={{ margin: '9px 0 0', fontSize: 13.5, color: 'var(--py-text-mid-alt)' }}>{t('Kampanyaların için özel uyarı kuralları tanımla ve üretilen uyarıları takip et.')}</p>
        </div>
        <button onClick={openModal} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.accent, color:'#fff', border: 'none', borderRadius:11, padding:'9px 16px', fontFamily: 'inherit', fontSize:13, fontWeight:700, cursor: 'pointer' }}>
          {t('Uyarı Oluştur')}
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 24, borderBottom: `1px solid ${T.border}` }}>
        {(['gecmis', 'kayitli'] as const).map(tb => (
          <span key={tb} onClick={() => setTab(tb)} style={{ padding: '0 0 12px', fontSize: 13.5, fontWeight: tab === tb ? 700 : 600, color: tab === tb ? T.text : 'var(--py-text-mid-alt)', cursor: 'pointer', borderBottom: tab === tb ? `2px solid ${T.accent}` : 'none', marginBottom: tab === tb ? -1 : 0 }}>
            {tb === 'kayitli' ? t('Kayıtlı Uyarılar') : t('Uyarı Geçmişi')}
          </span>
        ))}
      </div>

      {tab === 'kayitli' && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 14 }}>
        {[
          { label: 'Toplam Uyarı', value: total, color: T.text, bg: 'var(--py-blue-tint)', fg: T.blue, Ic: Icon.bellAlert },
          { label: 'Aktif', value: activeCount, color: T.greenText, bg: 'var(--py-green-tint)', fg: T.greenText, Ic: Icon.check },
          { label: 'Duraklatılmış', value: pausedCount, color: 'var(--py-text-mid-alt)', bg: 'var(--py-track-bg)', fg: 'var(--py-text-mid-alt)', Ic: Icon.pause },
          { label: 'Bugün Tetiklenen', value: triggeredToday, color: T.amberText, bg: 'var(--py-amber-tint-alt)', fg: T.amberText, Ic: Icon.warningTriangle },
          { label: 'Kritik', value: criticalCount, color: criticalCount > 0 ? T.redText : T.textFaint, bg: 'var(--py-red-tint)', fg: T.redText, Ic: Icon.target },
        ].map((s, i) => (
          <div key={i} style={{ border: `1px solid ${T.border}`, borderRadius: 14, background: 'var(--py-surface)', padding: '18px 19px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
              <span style={{ width: 26, height: 26, borderRadius: 8, background: s.bg, color: s.fg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><s.Ic size={14} sw={2} /></span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--py-text-mid-alt)' }}>{t(s.label)}</span>
            </div>
            <div style={{ fontSize: 27, fontWeight: 800, fontFamily: MONO, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
      )}

      {tab === 'kayitli' ? (
        loading ? (
          <PoyaisSpinner size={36} pad={24}/>
        ) : rules.length === 0 ? (
          <div style={{ fontSize: 13, color: T.textDim, padding: '24px 2px' }}>{t('Henüz uyarı kuralı yok.')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 11 }}>
            {rules.map(r => {
              const pill = STATUS_PILL[r.status] || STATUS_PILL['Aktif'];
              return (
                <div key={r.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, border: `1px solid ${T.border}`, borderRadius: 14, background: 'var(--py-surface)', padding: '16px 19px', opacity: pill.opacity ?? 1 }}>
                  <img src={PLATFORM_ICON_SRC[r.platform]} width={20} height={20} style={{ objectFit: 'contain', flexShrink: 0, marginTop: 3 }} />
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' as const, gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
                      <span style={{ fontSize: 13.5, fontWeight: 800 }}>{lang === 'en' && (r as any).title_en ? (r as any).title_en : r.title}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: pill.color, background: pill.bg, padding: '4px 10px', borderRadius: 999 }}>{t(r.status)}</span>
                      <SeverityIcon type={r.alert_type} />
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--py-text-mid-alt)' }}>{ruleMeta(r)}{r.last_value != null ? ` · ${t('Şu An')} ${r.last_value.toFixed(2)}` : ''}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--py-text-mid-alt)' }}>
                      <span style={{ border: `1px solid ${T.borderMid}`, borderRadius: 999, padding: '3px 10px', fontFamily: MONO, fontWeight: 700, color: T.text }}>{conditionPillText(r)}</span>
                      <span>·</span>
                      <span>{intervalLabel(r.check_interval)}</span>
                    </div>
                  </div>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <button onClick={() => setOpenMenuId(id => id === r.id ? null : r.id)} title={t('Seçenekler')}
                      style={{ width: 26, height: 26, borderRadius: 7, border: 'none', background: openMenuId === r.id ? 'var(--py-track-bg)' : 'transparent', color: T.textDim, cursor: 'pointer', fontSize: 15, lineHeight: 1 }}>⋯</button>
                    {openMenuId === r.id && (
                      <>
                        <div onClick={() => setOpenMenuId(null)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
                        <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 30, right: 0, background: 'var(--py-surface)', border: `1px solid ${T.border}`, borderRadius: 11, boxShadow: '0 4px 18px rgba(20,22,43,.12)', zIndex: 11, minWidth: 140, overflow: 'hidden' }}>
                          <button onClick={() => { setOpenMenuId(null); openEdit(r); }} style={{ display: 'block', width: '100%', textAlign: 'left' as const, padding: '9px 13px', border: 'none', background: 'none', fontSize: 12.5, fontWeight: 600, color: T.text, cursor: 'pointer' }}>{t('Düzenle')}</button>
                          <button onClick={() => { setOpenMenuId(null); togglePause(r); }} style={{ display: 'block', width: '100%', textAlign: 'left' as const, padding: '9px 13px', border: 'none', background: 'none', fontSize: 12.5, fontWeight: 600, color: T.text, cursor: 'pointer' }}>
                            {r.status === 'Duraklatıldı' ? t('Etkinleştir') : t('Duraklat')}
                          </button>
                          <button onClick={() => { setOpenMenuId(null); removeRule(r.id); }} style={{ display: 'block', width: '100%', textAlign: 'left' as const, padding: '9px 13px', border: 'none', background: 'none', fontSize: 12.5, fontWeight: 600, color: T.redText, cursor: 'pointer' }}>{t('Sil')}</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        historyLoading ? (
          <PoyaisSpinner size={36} pad={24}/>
        ) : history.length === 0 ? (
          <div style={{ fontSize: 13, color: T.textDim, padding: '24px 2px' }}>{t('Henüz tetiklenen uyarı yok.')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 11 }}>
            {history.map(h => {
              const sev = ALERT_TYPE_STYLE[h.alert_type] || ALERT_TYPE_STYLE.warning;
              const isAi = h.source === 'ai';
              return (
                <div key={`${h.source || 'rule'}-${h.id}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, border: `1px solid ${T.border}`, borderRadius: 14, background: 'var(--py-surface)', padding: '16px 19px' }}>
                  <span style={{ width: 30, height: 30, borderRadius: 10, background: sev.bg, color: sev.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {h.alert_type === 'info' ? <Icon.target size={15} /> : <SeverityIcon type={h.alert_type} size={15} />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' as const, gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
                      <span style={{ fontSize: 13.5, fontWeight: 800 }}>{h.title}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: sev.color, background: sev.bg, padding: '4px 10px', borderRadius: 999 }}>{t(sev.label)}</span>
                      {isAi && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 800, color: 'var(--py-amber-deep-text)', background: 'color-mix(in srgb, var(--py-accent) 14%, transparent)', padding: '3px 9px', borderRadius: 999 }}><Icon.sparkle size={11} sw={2} />Poyais One AI</span>}
                    </div>
                    {isAi ? (
                      <>
                        {h.description && <div style={{ fontSize: 12.5, color: 'var(--py-text-mid-alt)', lineHeight: 1.55 }}>{h.description}</div>}
                        {h.what_to_check && <div style={{ fontSize: 12.5, color: T.text, lineHeight: 1.55 }}><b>{t('Kontrol Et')}:</b> {h.what_to_check.replace(/^Kontrol edilecek:\s*/i, '')}</div>}
                        {h.evidence && <div style={{ fontSize: 11.5, color: T.textDim }}>{titleMetrics(h.evidence)}</div>}
                        {h.suggested_rule && <div style={{ fontSize: 11.5, color: T.textMid }}>{t('Önerilen Kural')}: <span style={{ fontFamily: MONO, fontWeight: 700 }}>{metricLabel(h.suggested_rule.platform, h.suggested_rule.metric)} {CONDITION_SYMBOLS[h.suggested_rule.condition_op]} {fmtThreshold(h.suggested_rule.metric, h.suggested_rule.threshold)}</span></div>}
                      </>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--py-text-mid-alt)' }}>
                        <img src={PLATFORM_ICON_SRC[h.platform]} width={13} height={13} style={{ objectFit: 'contain' }} />
                        <span style={{ fontFamily: MONO }}>{metricLabel(h.platform, h.metric)}: {h.actual_fmt} {h.condition_symbol} {h.threshold_fmt}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11.5, color: T.textDim }}>
                      <span>{timeAgo(h.triggered_at, lang)}.</span>
                      {!!h.email_sent && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: T.greenText, fontWeight: 600 }}>
                          <Icon.check size={12} sw={2.4} /> {t('E-posta gönderildi')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => (isAi ? acceptAi(h) : markSeen(h))} title={isAi && h.suggested_rule ? t('Uyarı olarak kaydet') : t('Görüldü olarak işaretle')}
                      aria-label={isAi && h.suggested_rule ? t('Uyarı olarak kaydet') : t('Görüldü olarak işaretle')}
                      style={{ width: 32, height: 32, borderRadius: 10, border: `1px solid ${T.borderMid}`, background: 'var(--py-surface)', color: T.greenText, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon.check size={16} sw={2.4} />
                    </button>
                    {isAi && (
                      <button onClick={() => markSeen(h)} title={t('Uyarıyı sil')} aria-label={t('Uyarıyı sil')}
                        style={{ width: 32, height: 32, borderRadius: 10, border: `1px solid ${T.borderMid}`, background: 'var(--py-surface)', color: T.redText, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {toast && <div style={{ position: 'fixed', bottom: 28, right: 28, zIndex: 10000, background: T.ink, color: '#fff', borderRadius: 12, padding: '12px 20px', fontSize: 12.5, fontWeight: 600, boxShadow: '0 12px 32px rgba(20,22,43,.25)' }}>{toast}</div>}

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,22,43,.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ background: 'var(--py-surface)', borderRadius: 16, padding: '26px 28px', maxWidth: 460, width: '100%', maxHeight: '88vh', overflowY: 'auto' as const, boxShadow: '0 20px 60px rgba(20,22,43,.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
              <span style={{ fontSize: 16.5, fontWeight: 800 }}>{editingId ? t('Uyarıyı Düzenle') : t('Uyarı Oluştur')}</span>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.textDim, lineHeight: 1 }}>×</button>
            </div>
            <p style={{ margin: '0 0 20px', fontSize: 12.5, color: 'var(--py-text-mid-alt)' }}>{t('Reklam kampanyalarını izlemek için özel bir uyarı kur.')}</p>

            <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 14 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('UYARI ADI')}</div>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder={t('örn. Yüksek Harcama Uyarısı')}
                  style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const }} />
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('PLATFORM')}</div>
                <select value={form.platform} onChange={e => { const p = e.target.value; setForm(f => ({ ...f, platform: p, scope: '', metric: (METRICS[p]||METRICS.gads)[0].value })); loadCampaigns(p); }}
                  style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: 'var(--py-surface)' }}>
                  {(PLATFORMS.filter(p => platformConnected(p.value)).length ? PLATFORMS.filter(p => platformConnected(p.value)) : PLATFORMS).map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              {form.platform !== 'ga4' && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('KAPSAM')}</div>
                  <select value={form.scope} onChange={e => { const id = e.target.value; const c = campaignOptions.find(x => x.id === id); setForm(f => ({ ...f, scope: id, scopeLabel: c?.name || '' })); }}
                    style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: 'var(--py-surface)' }}>
                    <option value="">{t('Tüm kampanyalar')}</option>
                    {campaignsLoading && <option disabled>{t('Yükleniyor…')}</option>}
                    {campaignOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('METRİK')}</div>
                  <select value={form.metric} onChange={e => setForm(f => ({ ...f, metric: e.target.value }))}
                    style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: 'var(--py-surface)' }}>
                    {metricOptions.map(m => <option key={m.value} value={m.value}>{t(m.label)}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('KOŞUL')}</div>
                  <select value={form.condition_op} onChange={e => setForm(f => ({ ...f, condition_op: e.target.value }))}
                    style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: 'var(--py-surface)' }}>
                    {CONDITIONS.map(c => <option key={c.value} value={c.value}>{t(c.label)}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('EŞİK')}</div>
                  <input type="number" value={form.threshold} onChange={e => setForm(f => ({ ...f, threshold: e.target.value }))} placeholder={t('örn. 5000')}
                    style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('KONTROL ARALIĞI')}</div>
                  <select value={form.check_interval} onChange={e => setForm(f => ({ ...f, check_interval: e.target.value }))}
                    style={{ width: '100%', border: `1px solid ${T.borderMid}`, borderRadius: 10, padding: '10px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: 'var(--py-surface)' }}>
                    {INTERVALS.map(iv => <option key={iv.value} value={iv.value}>{t(iv.label)}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--py-text-mid-alt)', marginBottom: 6 }}>{t('UYARI TÜRÜ')}</div>
                <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 8 }}>
                  {ALERT_TYPES.map(at => (
                    <div key={at.value} onClick={() => setForm(f => ({ ...f, alert_type: at.value }))}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1px solid ${form.alert_type === at.value ? at.color : T.borderMid}`, background: form.alert_type === at.value ? alpha(at.color, 7) : 'var(--py-surface)', borderRadius: 10, padding: '9px 12px', cursor: 'pointer' }}>
                      <span style={{ width: 14, height: 14, borderRadius: '50%', border: `4px solid ${form.alert_type === at.value ? at.color : 'var(--py-border-soft)'}`, background: 'var(--py-surface)', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text }}>{t(at.label)}</div>
                        <div style={{ fontSize: 11, color: 'var(--py-text-mid-alt)' }}>{t(at.desc)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ fontSize: 11.5, color: T.textDim, marginTop: 12, lineHeight: 1.5 }}>{t('Karşılaştırılan değer son 7 günün toplamı (oran metriklerinde ortalaması) olur.')}</div>
            {createErr && <div style={{ fontSize: 12, color: T.redText, marginTop: 10 }}>{createErr}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${T.border}`, background: 'var(--py-surface)', color: T.textMid, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{t('Vazgeç')}</button>
              <button onClick={createRule} disabled={saving || !form.threshold} style={{ flex: 1, padding:'9px 16px', borderRadius:11, border: 'none', background: T.accent, color:'#fff', fontSize:13, fontWeight:700, cursor: 'pointer', opacity: saving || !form.threshold ? 0.6 : 1 }}>
                {saving ? (editingId ? t('Kaydediliyor…') : t('Oluşturuluyor…')) : (editingId ? t('Kaydet') : t('Uyarı Oluştur'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
